import React, { useEffect, useRef } from "react";

export const DynamicGridBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationFrameId;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    // Mouse coordinate targets
    const mouse = {
      x: width / 2,
      y: height / 2,
      targetX: width / 2,
      targetY: height / 2,
    };

    // Tracks mouse position
    const handleMouseMove = (e) => {
      mouse.targetX = e.clientX;
      mouse.targetY = e.clientY;
    };

    // Handles window resizing
    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("resize", handleResize);

    const drawGrid = () => {
      if (!ctx || !canvas) return;

      // Clear the canvas on every frame so it remains transparent and allows Lottie stars to show through
      ctx.clearRect(0, 0, width, height);

      // 1. Draw subtle ambient glow in the center (Signature Purple #9067C6)
      const ambient = ctx.createRadialGradient(
        width / 2, height / 2, 0,
        width / 2, height / 2, Math.max(width, height) * 0.75
      );
      ambient.addColorStop(0, "rgba(144, 103, 198, 0.12)"); // signature purple
      ambient.addColorStop(1, "rgba(3, 25, 38, 0)");
      ctx.fillStyle = ambient;
      ctx.fillRect(0, 0, width, height);

      // 2. Cursor Interpolation (smooth fluid movement trailing)
      mouse.x += (mouse.targetX - mouse.x) * 0.08;
      mouse.y += (mouse.targetY - mouse.y) * 0.08;

      // 3. Radial Spotlight under the Cursor (Neon blue and signature purple mix)
      const glowRadius = 380;
      const glowGrad = ctx.createRadialGradient(
        mouse.x, mouse.y, 0,
        mouse.x, mouse.y, glowRadius
      );
      glowGrad.addColorStop(0, "rgba(96, 165, 250, 0.16)"); // neon blue core
      glowGrad.addColorStop(0.4, "rgba(144, 103, 198, 0.07)"); // signature purple mid-vignette
      glowGrad.addColorStop(1, "rgba(0, 0, 0, 0)"); // transparent fade
      ctx.fillStyle = glowGrad;
      ctx.fillRect(0, 0, width, height);

      // 4. Dot Grid Drawing & Mouse Proximity Morphing
      const dotSpacing = 28; // space between dots
      const influenceRadius = 220; // cursor proximity radius
      for (let x = 0; x < width; x += dotSpacing) {
        for (let y = 0; y < height; y += dotSpacing) {
          const dx = x - mouse.x;
          const dy = y - mouse.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < influenceRadius) {
            // Proximity math: closer dots are larger and brighter
            const factor = 1 - dist / influenceRadius;
            const size = 1.2 + factor * 2.2; // dot size grows from 1.2px to 3.4px
            // Glow color morphing from slate-blue to neon-blue/purple
            ctx.fillStyle = `rgba(96, 165, 250, ${0.08 + factor * 0.45})`;
            ctx.beginPath();
            ctx.arc(x, y, size, 0, Math.PI * 2);
            ctx.fill();
          } else {
            // Static dot grid
            ctx.fillStyle = `rgba(148, 163, 184, 0.07)`;
            ctx.beginPath();
            ctx.arc(x, y, 1.2, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }

      animationFrameId = requestAnimationFrame(drawGrid);
    };

    drawGrid();

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: -1 }}
    />
  );
};
